import DOMPurify from 'dompurify';
import { useOne, useCustomMutation } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import {
  FileText,
  Eye,
  Heart,
  MessageCircle,
  User,
  Image as ImageIcon,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  ShieldAlert,
  Pencil,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { FormHeader } from '@/components/forms';
import { Separator } from '@/components/ui/separator';
import { EntityAnalyticsCard, EntityAnalyticsGrid } from '@/components/shared';
import type { Post, ContentStatus } from './types';

function getStatusVariant(status: ContentStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'PENDING_MODERATION': return 'secondary';
    case 'PUBLISHED': return 'default';
    case 'REJECTED': return 'destructive';
    case 'DRAFT': return 'outline';
    default: return 'secondary';
  }
}

function getStatusLabel(status: ContentStatus): string {
  switch (status) {
    case 'PENDING_MODERATION': return 'На модерации';
    case 'PUBLISHED': return 'Опубликован';
    case 'REJECTED': return 'Отклонён';
    case 'DRAFT': return 'Черновик';
    default: return status;
  }
}

function getStatusIcon(status: ContentStatus) {
  switch (status) {
    case 'PENDING_MODERATION': return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'PUBLISHED': return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'REJECTED': return <XCircle className="h-4 w-4 text-destructive" />;
    case 'DRAFT': return <FileText className="h-4 w-4 text-muted-foreground" />;
    default: return null;
  }
}

function getPostAuthorName(post: Post): string {
  if (post.teacher) { return post.teacher.name; }
  if (post.author) {
    const parts: string[] = [];
    if (post.author.firstName) { parts.push(post.author.firstName); }
    if (post.author.lastName) { parts.push(post.author.lastName); }
    if (parts.length > 0) { return parts.join(' '); }
    if (post.author.username) { return post.author.username; }
  }
  return 'Неизвестный';
}

function getPostAuthorPhoto(post: Post): string | null {
  if (post.teacher) { return post.teacher.photoUrl; }
  if (post.author) { return post.author.photoUrl; }
  return null;
}

function getPostAuthorRole(post: Post): string | null {
  if (post.teacher) { return post.teacher.role; }
  if (post.author) { return 'Пользователь'; }
  return null;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PostShow() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const postResult = useOne<Post>({
    resource: 'posts',
    id: id!,
  });
  const isLoading = postResult.query.isLoading;
  const refetch = postResult.query.refetch;

  const { mutate: changeStatus, mutation: statusMutation } = useCustomMutation();
  const statusLoading = statusMutation.isPending;

  const post = postResult.result;

  const handleStatusChange = (newStatus: string) => {
    if (!post) {return;}

    if (newStatus === 'REJECTED') {
      setRejectOpen(true);
      return;
    }

    changeStatus(
      {
        url: `/admin/posts/${post.id}/status`,
        method: 'patch',
        values: { status: newStatus },
      },
      { onSuccess: () => refetch() }
    );
  };

  const handleReject = () => {
    if (!post || !rejectionReason.trim()) {return;}

    changeStatus(
      {
        url: `/admin/posts/${post.id}/status`,
        method: 'patch',
        values: { status: 'REJECTED', rejectionReason },
      },
      {
        onSuccess: () => {
          setRejectOpen(false);
          setRejectionReason('');
          refetch();
        },
      }
    );
  };

  if (isLoading || !post) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FormHeader
        title={`Пост от ${getPostAuthorName(post)}`}
        description={`Создан ${formatDate(post.createdAt)}`}
        onBack={() => navigate('/posts')}
        sticky
        actions={
          <Button onClick={() => navigate(`/posts/edit/${post.id}`)}>
            <Pencil className="h-4 w-4 mr-2" />
            Редактировать
          </Button>
        }
      />

      <EntityAnalyticsGrid>
        <EntityAnalyticsCard
          title="Просмотры"
          value={post.viewsCount}
          icon={<Eye className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Лайки"
          value={post.likesCount}
          icon={<Heart className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Комментарии"
          value={post.commentsCount}
          icon={<MessageCircle className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Статус"
          value={getStatusLabel(post.status)}
          icon={getStatusIcon(post.status)}
        />
      </EntityAnalyticsGrid>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Левая колонка — контент */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Контент</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
              />
            </CardContent>
          </Card>

          {post.images && post.images.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  Изображения ({post.images.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {post.images.map((image) => (
                    <a
                      key={image.id}
                      href={image.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img
                        src={image.url}
                        alt=""
                        className="w-full h-40 object-cover rounded-lg border hover:opacity-90 transition-opacity"
                      />
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

        </div>

        {/* Правая колонка */}
        <div className="space-y-6">
          {/* Управление статусом */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldAlert className="h-4 w-4" />
                Управление
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  {getStatusIcon(post.status)}
                  <Badge variant={getStatusVariant(post.status)}>
                    {getStatusLabel(post.status)}
                  </Badge>
                </div>

                <Select
                  value={post.status}
                  onValueChange={handleStatusChange}
                  disabled={statusLoading}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PUBLISHED">Опубликован</SelectItem>
                    <SelectItem value="DRAFT">Черновик (скрыть)</SelectItem>
                    <SelectItem value="REJECTED">Отклонён (с причиной)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {post.rejectionReason && (
                <>
                  <Separator />
                  <div className="rounded-md bg-destructive/10 p-3">
                    <p className="text-sm font-medium text-destructive">Причина отклонения:</p>
                    <p className="text-sm text-destructive/80 mt-1">{post.rejectionReason}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Автор */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Автор
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                {getPostAuthorPhoto(post) ? (
                  <img
                    src={getPostAuthorPhoto(post)!}
                    alt=""
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    <User className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <p className="font-medium">{getPostAuthorName(post)}</p>
                  {getPostAuthorRole(post) && (
                    <p className="text-sm text-muted-foreground">{getPostAuthorRole(post)}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Статистика */}
          <Card>
            <CardHeader>
              <CardTitle>Статистика</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Eye className="h-4 w-4" />
                    <span className="text-sm">Просмотры</span>
                  </div>
                  <span className="font-medium">{post.viewsCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Heart className="h-4 w-4" />
                    <span className="text-sm">Лайки</span>
                  </div>
                  <span className="font-medium">{post.likesCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MessageCircle className="h-4 w-4" />
                    <span className="text-sm">Комментарии</span>
                  </div>
                  <span className="font-medium">{post.commentsCount}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Метаданные */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Метаданные
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Создан</span>
                  <span className="text-sm font-medium">{formatDate(post.createdAt)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Обновлён</span>
                  <span className="text-sm font-medium">{formatDate(post.updatedAt)}</span>
                </div>
                {post.publishedAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Опубликован</span>
                    <span className="text-sm font-medium">{formatDate(post.publishedAt)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Диалог отклонения */}
      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отклонить пост?</AlertDialogTitle>
            <AlertDialogDescription>
              Укажите причину отклонения. Автор получит уведомление с указанной причиной.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Причина отклонения..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={3}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              disabled={!rejectionReason.trim() || statusLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Отклонить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
